import React, { useState } from 'react';
import { Save, X, Package2, Star } from 'lucide-react';

const ProductSaveFromAnalysis = ({ 
  analysisResult, 
  productImage, 
  onSave, 
  onCancel 
}) => {
  const [formData, setFormData] = useState({
    name: extractProductName(analysisResult) || '',
    brand: extractProductBrand(analysisResult) || '',
    category: extractProductCategory(analysisResult) || 'Skincare',
    notes: extractProductNotes(analysisResult) || '',
    rating: null,
    image: productImage || null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [
    'Skincare',
    'Makeup',
    'Hair Care',
    'Body Care',
    'Fragrance',
    'Tools & Accessories'
  ];

  function extractProductName(analysis) {
    if (!analysis) return '';
    
    // Try to extract product name from analysis text
    const namePatterns = [
      /Product name:\s*([^\n\r]+)/i,
      /Name:\s*([^\n\r]+)/i,
      /Product:\s*([^\n\r]+)/i,
      /This is a\s+([^,]+)/i,
      /I can see.*?([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i
    ];
    
    for (const pattern of namePatterns) {
      const match = analysis.match(pattern);
      if (match && match[1] && 
          !match[1].toLowerCase().includes('not clearly visible') &&
          match[1].trim().length > 2) {
        return match[1].trim();
      }
    }
    
    return '';
  }

  function extractProductBrand(analysis) {
    if (!analysis) return '';
    
    const brandPatterns = [
      /Brand name:\s*([^\n\r]+)/i,
      /Brand:\s*([^\n\r]+)/i,
      /by\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i,
      /from\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i
    ];
    
    for (const pattern of brandPatterns) {
      const match = analysis.match(pattern);
      if (match && match[1] && 
          !match[1].toLowerCase().includes('not clearly visible') &&
          match[1].trim().length > 2) {
        return match[1].trim();
      }
    }
    
    return '';
  }

  function extractProductCategory(analysis) {
    if (!analysis) return 'Skincare';
    
    const categoryKeywords = {
      'Skincare': ['skincare', 'moisturizer', 'serum', 'cleanser', 'toner', 'sunscreen'],
      'Makeup': ['makeup', 'foundation', 'concealer', 'lipstick', 'mascara', 'blush'],
      'Hair Care': ['hair', 'shampoo', 'conditioner', 'hair care'],
      'Body Care': ['body', 'lotion', 'body care'],
      'Fragrance': ['perfume', 'fragrance', 'scent']
    };
    
    const analysisLower = analysis.toLowerCase();
    
    for (const [category, keywords] of Object.entries(categoryKeywords)) {
      if (keywords.some(keyword => analysisLower.includes(keyword))) {
        return category;
      }
    }
    
    return 'Skincare';
  }

  function extractProductNotes(analysis) {
    if (!analysis) return '';
    
    // Extract key insights from analysis
    const insights = [];
    
    // Look for benefits
    const benefitPatterns = [
      /benefits?[:\s]+([^.\n]+)/i,
      /good for[:\s]+([^.\n]+)/i,
      /helps with[:\s]+([^.\n]+)/i
    ];
    
    for (const pattern of benefitPatterns) {
      const match = analysis.match(pattern);
      if (match && match[1]) {
        insights.push(`Benefits: ${match[1].trim()}`);
      }
    }
    
    // Look for ingredients
    const ingredientPatterns = [
      /ingredients?[:\s]+([^.\n]+)/i,
      /contains[:\s]+([^.\n]+)/i
    ];
    
    for (const pattern of ingredientPatterns) {
      const match = analysis.match(pattern);
      if (match && match[1]) {
        insights.push(`Ingredients: ${match[1].trim()}`);
      }
    }
    
    return insights.join(' | ');
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = () => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => handleInputChange('rating', star)}
            className={`transition-colors ${
              star <= (formData.rating || 0) 
                ? 'text-yellow-400' 
                : 'text-gray-300 hover:text-yellow-300'
            }`}
          >
            <Star className="h-4 w-4 fill-current" />
          </button>
        ))}
        {formData.rating && (
          <span className="text-xs text-[#2A324B]/70 ml-1">
            {formData.rating}/5
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#F3D5B5] rounded-lg flex items-center justify-center">
              <Save className="h-4 w-4 text-[#2A324B]" />
            </div>
            <h3 className="text-lg font-semibold text-[#2A324B]">Save Product from Analysis</h3>
          </div>
          <button
            onClick={onCancel}
            className="text-[#2A324B]/50 hover:text-[#2A324B] transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* AI Analysis Preview */}
        {analysisResult && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2">
              <div className="text-blue-600 text-sm">🤖</div>
              <div className="text-sm text-blue-800">
                <strong>AI Analysis:</strong> {analysisResult.substring(0, 150)}...
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Product Name */}
          <div>
            <label className="block text-sm font-medium text-[#2A324B] mb-2">
              Product Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Vitamin C Serum"
              className="w-full px-3 py-2 border border-[#341C1C]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3D5B5]"
            />
          </div>

          {/* Brand */}
          <div>
            <label className="block text-sm font-medium text-[#2A324B] mb-2">
              Brand *
            </label>
            <input
              type="text"
              value={formData.brand}
              onChange={(e) => handleInputChange('brand', e.target.value)}
              placeholder="e.g., The Ordinary"
              className="w-full px-3 py-2 border border-[#341C1C]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3D5B5]"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-[#2A324B] mb-2">
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-[#341C1C]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3D5B5]"
            >
              {categories.map(category => (
                <option key={category} value={category.toLowerCase()}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-[#2A324B] mb-2">
              Your Rating (Optional)
            </label>
            {renderStars()}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[#2A324B] mb-2">
              Notes (Pre-filled from AI analysis)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="AI analysis insights..."
              rows={3}
              className="w-full px-3 py-2 border border-[#341C1C]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3D5B5] resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-[#341C1C]/20 rounded-lg text-[#2A324B] hover:bg-[#F3D5B5]/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.name || !formData.brand}
              className="flex-1 px-4 py-2 bg-[#F3D5B5] text-[#2A324B] rounded-lg hover:bg-[#F3D5B5]/80 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save to Catalog'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductSaveFromAnalysis;
